import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material'
import EventIcon          from '@mui/icons-material/EventOutlined'
import EventAvailableIcon from '@mui/icons-material/EventAvailableOutlined'
import AddIcon            from '@mui/icons-material/Add'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'
import { readFunctionError } from '../../lib/functionError'

// Office-manager workspace for all meetings:
//
//   Requests to schedule       — pending; OM negotiates with parent then sets time
//   Scheduled meetings         — confirmed; OM can edit time or cancel
//   Past — pending mark-held   — meeting time has passed but no summary recorded
//
// Plus a "+ New meeting" dialog for kickoff / parent-requested / other.

type MeetingKind   = 'kickoff' | 'step1_review' | 'parent_requested' | 'other'
type MeetingStatus = 'requested' | 'scheduled' | 'held' | 'cancelled'

interface MeetingRow {
  meeting_id:    string
  student_id:    string
  student_name:  string
  mentor_id:     string | null
  mentor_name:   string | null
  kind:          MeetingKind
  status:        MeetingStatus
  requested_at:  string
  request_note:  string | null
  scheduled_at:  string | null
  scheduled_note: string | null
  project_id:    string | null
}

interface StudentOption {
  student_id: string
  user_id:    string
  label:      string  // "First Last (email)"
  lead_mentor_id: string | null
}

interface MentorOption {
  user_id: string
  label:   string
}

const KIND_LABEL: Record<MeetingKind, string> = {
  kickoff:           'Kickoff',
  step1_review:      'Step 1 review',
  parent_requested:  'Parent-requested',
  other:             'Other',
}
const KIND_COLOR: Record<MeetingKind, 'primary' | 'success' | 'warning' | 'default'> = {
  kickoff:           'success',
  step1_review:      'primary',
  parent_requested:  'warning',
  other:             'default',
}

function timeSince(s: string) {
  const days = Math.floor((Date.now() - new Date(s).getTime()) / 864e5)
  if (days <= 0) return 'today'
  if (days === 1) return '1 day ago'
  return `${days} days ago`
}

function fmtDateTime(s: string) {
  return new Date(s).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZone: 'America/Los_Angeles',
  })
}

function isoToLocalInput(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default function MeetingsPanel() {
  const { session, role } = useAuth()
  const navigate = useNavigate()
  const [rows,    setRows]    = useState<MeetingRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [editFor,    setEditFor]    = useState<MeetingRow | null>(null)
  const [cancelFor,  setCancelFor]  = useState<MeetingRow | null>(null)
  const [holdFor,    setHoldFor]    = useState<MeetingRow | null>(null)

  async function load() {
    if (!session) return
    setLoading(true); setError(null)
    try {
      const { data: meetings } = await supabase
        .from('meeting')
        .select('meeting_id, student_id, mentor_id, kind, status, requested_at, request_note, scheduled_at, scheduled_note, project_id')
        .in('status', ['requested', 'scheduled'])
        .order('requested_at', { ascending: true })

      if (!meetings || meetings.length === 0) { setRows([]); setLoading(false); return }

      const studentIds = Array.from(new Set(meetings.map(m => m.student_id)))
      const mentorIds  = Array.from(new Set(meetings.map(m => m.mentor_id).filter(Boolean) as string[]))

      const [{ data: profiles }, { data: mentors }] = await Promise.all([
        supabase.from('student_profile').select('student_id, user_id').in('student_id', studentIds),
        mentorIds.length > 0
          ? supabase.from('users').select('user_id, first_name, last_name').in('user_id', mentorIds)
          : Promise.resolve({ data: [] as { user_id: string; first_name: string; last_name: string }[] }),
      ])
      const userIds = (profiles ?? []).map(p => p.user_id)
      const { data: students } = userIds.length > 0
        ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', userIds)
        : { data: [] as { user_id: string; first_name: string; last_name: string }[] }
      const studentMap = Object.fromEntries((profiles ?? []).map(p => [p.student_id, p.user_id]))
      const userMap    = Object.fromEntries((students ?? []).map(u => [u.user_id, `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()]))
      const mentorMap  = Object.fromEntries((mentors  ?? []).map(u => [u.user_id, `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()]))

      setRows((meetings as any[]).map(m => ({
        meeting_id:     m.meeting_id,
        student_id:     m.student_id,
        student_name:   userMap[studentMap[m.student_id]] ?? '—',
        mentor_id:      m.mentor_id,
        mentor_name:    m.mentor_id ? (mentorMap[m.mentor_id] ?? null) : null,
        kind:           m.kind,
        status:         m.status,
        requested_at:   m.requested_at,
        request_note:   m.request_note,
        scheduled_at:   m.scheduled_at,
        scheduled_note: m.scheduled_note,
        project_id:     m.project_id,
      })))
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [session])

  const requested = useMemo(() => rows.filter(r => r.status === 'requested'), [rows])
  const scheduled = useMemo(() => rows.filter(r => r.status === 'scheduled'), [rows])
  const pastUnheld = useMemo(() => {
    const now = Date.now()
    return scheduled.filter(r => r.scheduled_at && new Date(r.scheduled_at).getTime() < now)
  }, [scheduled])
  const upcomingScheduled = useMemo(() => {
    const now = Date.now()
    return scheduled.filter(r => r.scheduled_at && new Date(r.scheduled_at).getTime() >= now)
  }, [scheduled])

  if (role !== 'admin' && role !== 'office_manager') return null

  const total = rows.length

  return (
    <Box sx={{
      mb: 3, p: 2.5, borderRadius: 2,
      border: '1px solid', borderColor: requested.length > 0 ? 'warning.light' : 'divider',
      bgcolor: requested.length > 0 ? 'rgba(237,108,2,0.04)' : 'background.paper',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <EventIcon fontSize="small" sx={{ color: 'warning.main' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>Meetings</Typography>
        <Chip size="small" label={total} color={requested.length > 0 ? 'warning' : 'default'} sx={{ minWidth: 32, fontWeight: 700, mr: 1 }} />
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New meeting
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
      ) : total === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No pending or scheduled meetings. Use "New meeting" to create one.
        </Typography>
      ) : (
        <>
          {/* Requests */}
          {requested.length > 0 && (
            <Section title="Requests to schedule" count={requested.length} color="warning">
              {requested.map(r => (
                <Row key={r.meeting_id} row={r}>
                  <Button size="small" variant="contained" color="primary" onClick={() => setEditFor(r)}>
                    Set schedule
                  </Button>
                  {r.project_id && (
                    <Button size="small" variant="text" onClick={() => navigate(`/review/${r.project_id}`)}>
                      View submission
                    </Button>
                  )}
                  <Button size="small" variant="text" color="warning" onClick={() => setCancelFor(r)}>Cancel</Button>
                </Row>
              ))}
            </Section>
          )}

          {/* Past — pending mark-held */}
          {pastUnheld.length > 0 && (
            <Section title="Past — pending mark-held" count={pastUnheld.length} color="warning" icon={<EventAvailableIcon fontSize="small" sx={{ color: 'warning.main' }} />}>
              {pastUnheld.map(r => (
                <Row key={r.meeting_id} row={r}>
                  <Button size="small" variant="outlined" onClick={() => setHoldFor(r)}>
                    Mark held
                  </Button>
                  <Button size="small" variant="text" onClick={() => setEditFor(r)}>Edit time</Button>
                  <Button size="small" variant="text" color="warning" onClick={() => setCancelFor(r)}>Cancel</Button>
                </Row>
              ))}
            </Section>
          )}

          {/* Upcoming scheduled */}
          {upcomingScheduled.length > 0 && (
            <Section title="Scheduled meetings" count={upcomingScheduled.length} color="info" icon={<EventAvailableIcon fontSize="small" sx={{ color: 'info.main' }} />}>
              {upcomingScheduled.map(r => (
                <Row key={r.meeting_id} row={r}>
                  <Button size="small" variant="outlined" onClick={() => setEditFor(r)}>Edit time</Button>
                  {r.project_id && (
                    <Button size="small" variant="text" onClick={() => navigate(`/review/${r.project_id}`)}>
                      View submission
                    </Button>
                  )}
                  <Button size="small" variant="text" color="warning" onClick={() => setCancelFor(r)}>Cancel</Button>
                </Row>
              ))}
            </Section>
          )}
        </>
      )}

      <CreateMeetingDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => { setCreateOpen(false); load() }}
      />
      <EditScheduleDialog
        row={editFor}
        onClose={() => setEditFor(null)}
        onSaved={() => { setEditFor(null); load() }}
      />
      <CancelDialog
        row={cancelFor}
        onClose={() => setCancelFor(null)}
        onSaved={() => { setCancelFor(null); load() }}
      />
      <MarkHeldDialog
        row={holdFor}
        onClose={() => setHoldFor(null)}
        onSaved={() => { setHoldFor(null); load() }}
      />
    </Box>
  )
}

// ── primitives ───────────────────────────────────────────────────────────────

function Section({
  title, count, color, icon, children,
}: { title: string; count: number; color: 'warning' | 'info'; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 0.5 }}>
        {icon}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>{title}</Typography>
        <Chip size="small" label={count} color={color} sx={{ minWidth: 32, fontWeight: 700 }} />
      </Box>
      {children}
    </Box>
  )
}

function Row({ row, children }: { row: MeetingRow; children: React.ReactNode }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25,
      borderTop: '1px solid', borderColor: 'divider',
      '&:first-of-type': { borderTop: 'none' },
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25, flexWrap: 'wrap' }}>
          <Chip size="small" label={KIND_LABEL[row.kind]} color={KIND_COLOR[row.kind]} variant="outlined" />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.student_name}
            {row.mentor_name && (
              <Typography component="span" variant="caption" color="text.secondary">
                {' '}— with {row.mentor_name}
              </Typography>
            )}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {row.scheduled_at ? fmtDateTime(row.scheduled_at) : `Requested ${timeSince(row.requested_at)}`}
        </Typography>
        {row.request_note && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
            "{row.request_note}"
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
        {children}
      </Box>
    </Box>
  )
}

// ── Create meeting dialog ────────────────────────────────────────────────────

function CreateMeetingDialog({
  open, onClose, onSaved,
}: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const [kind,        setKind]       = useState<MeetingKind>('kickoff')
  const [student,     setStudent]    = useState<StudentOption | null>(null)
  const [students,    setStudents]   = useState<StudentOption[]>([])
  const [mentor,      setMentor]     = useState<MentorOption | null>(null)
  const [mentors,     setMentors]    = useState<MentorOption[]>([])
  const [scheduleAt,  setScheduleAt] = useState('')
  const [requestNote, setRequestNote] = useState('')
  const [scheduledNote, setScheduledNote] = useState('')
  const [saving,      setSaving]      = useState(false)
  const [error,       setError]       = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setKind('kickoff'); setStudent(null); setMentor(null)
    setScheduleAt(''); setRequestNote(''); setScheduledNote('')
    setError(null)

    async function loadOptions() {
      const [{ data: profiles }, { data: mentorRows }, { data: leads }] = await Promise.all([
        supabase.from('student_profile').select('student_id, user_id'),
        supabase.from('users').select('user_id, first_name, last_name, role').in('role', ['mentor', 'instructor']).eq('status', 'active'),
        supabase.from('student_mentor').select('student_id, mentor_id').eq('mentor_role', 'lead').eq('status', 'active'),
      ])
      const userIds = (profiles ?? []).map(p => p.user_id)
      const { data: studentUsers } = userIds.length > 0
        ? await supabase.from('users').select('user_id, first_name, last_name, email').in('user_id', userIds)
        : { data: [] as { user_id: string; first_name: string; last_name: string; email: string }[] }
      const userMap = Object.fromEntries((studentUsers ?? []).map(u => [u.user_id, u]))
      const leadMap = Object.fromEntries((leads ?? []).map(l => [l.student_id, l.mentor_id]))

      setStudents((profiles ?? []).map(p => {
        const u = userMap[p.user_id]
        const name = u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : ''
        return {
          student_id:     p.student_id,
          user_id:        p.user_id,
          label:          `${name || '(no name)'} (${u?.email ?? '—'})`,
          lead_mentor_id: leadMap[p.student_id] ?? null,
        }
      }).sort((a, b) => a.label.localeCompare(b.label)))

      setMentors((mentorRows ?? []).map(m => ({
        user_id: m.user_id,
        label:   `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || m.user_id,
      })).sort((a, b) => a.label.localeCompare(b.label)))
    }
    loadOptions()
  }, [open])

  // Auto-suggest the student's lead mentor
  useEffect(() => {
    if (student?.lead_mentor_id) {
      const m = mentors.find(m => m.user_id === student.lead_mentor_id)
      if (m) setMentor(m)
    }
  }, [student, mentors])

  async function save() {
    if (!student) { setError('Pick a student.'); return }
    let scheduledIso: string | undefined
    if (scheduleAt) {
      const ts = new Date(scheduleAt).getTime()
      if (isNaN(ts)) { setError('Invalid date/time.'); return }
      if (ts < Date.now()) { setError('Time must be in the future.'); return }
      scheduledIso = new Date(scheduleAt).toISOString()
    }
    setSaving(true); setError(null)
    const { data, error: err } = await supabase.functions.invoke('create-meeting', {
      body: {
        student_id:     student.student_id,
        mentor_id:      mentor?.user_id ?? null,
        kind,
        scheduled_at:   scheduledIso,
        request_note:   requestNote.trim() || undefined,
        scheduled_note: scheduledNote.trim() || undefined,
      },
    })
    setSaving(false)
    if (err)         { setError(await readFunctionError(err)); return }
    if (data?.error) { setError(data.error); return }
    onSaved()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New meeting</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="kind-label">Kind</InputLabel>
          <Select
            labelId="kind-label" label="Kind" value={kind}
            onChange={e => setKind(e.target.value as MeetingKind)}
          >
            <MenuItem value="kickoff">Kickoff</MenuItem>
            <MenuItem value="parent_requested">Parent-requested</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>

        <Autocomplete
          options={students}
          value={student}
          onChange={(_, v) => setStudent(v)}
          getOptionLabel={o => o.label}
          isOptionEqualToValue={(a, b) => a.student_id === b.student_id}
          renderInput={params => <TextField {...params} size="small" label="Student" required />}
          sx={{ mb: 2 }}
        />

        <Autocomplete
          options={mentors}
          value={mentor}
          onChange={(_, v) => setMentor(v)}
          getOptionLabel={o => o.label}
          isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
          renderInput={params => <TextField {...params} size="small" label="Mentor (optional)" />}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth size="small"
          type="datetime-local"
          label="Date and time (leave blank to schedule later)"
          value={scheduleAt}
          onChange={e => setScheduleAt(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth multiline rows={2} size="small"
          label="Note for the office (why this meeting; parent context)"
          value={requestNote}
          onChange={e => setRequestNote(e.target.value)}
          sx={{ mb: 2 }}
        />

        {scheduleAt && (
          <TextField
            fullWidth multiline rows={2} size="small"
            label="Note for the family (location, Zoom, agenda)"
            value={scheduledNote}
            onChange={e => setScheduledNote(e.target.value)}
            sx={{ mb: 1 }}
          />
        )}

        <Typography variant="caption" color="text.secondary">
          {scheduleAt
            ? 'Student and parent(s) will receive an email confirmation immediately.'
            : 'No emails sent yet — set a time later to confirm.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || !student}>
          {saving ? <CircularProgress size={18} /> : (scheduleAt ? 'Create & email' : 'Create request')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Edit / set schedule dialog ───────────────────────────────────────────────

function EditScheduleDialog({
  row, onClose, onSaved,
}: { row: MeetingRow | null; onClose: () => void; onSaved: () => void }) {
  const [scheduleAt, setScheduleAt] = useState('')
  const [note,       setNote]       = useState('')
  const [saving,     setSaving]     = useState(false)
  const [error,      setError]      = useState<string | null>(null)

  useEffect(() => {
    if (row) {
      setScheduleAt(row.scheduled_at ? isoToLocalInput(row.scheduled_at) : '')
      setNote('')
      setError(null)
    }
  }, [row])

  async function save() {
    if (!row) return
    if (!scheduleAt) { setError('Pick a date and time.'); return }
    const ts = new Date(scheduleAt).getTime()
    if (isNaN(ts)) { setError('Invalid date/time.'); return }
    if (ts < Date.now()) { setError('Time must be in the future.'); return }
    setSaving(true); setError(null)
    const { data, error: err } = await supabase.functions.invoke('update-meeting', {
      body: {
        meeting_id:     row.meeting_id,
        scheduled_at:   new Date(scheduleAt).toISOString(),
        scheduled_note: note.trim() || undefined,
      },
    })
    setSaving(false)
    if (err)         { setError(await readFunctionError(err)); return }
    if (data?.error) { setError(data.error); return }
    onSaved()
  }

  const editing = !!row?.scheduled_at

  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{editing ? 'Edit meeting time' : 'Set meeting schedule'}</DialogTitle>
      <DialogContent>
        {row && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <Chip size="small" label={KIND_LABEL[row.kind]} color={KIND_COLOR[row.kind]} variant="outlined" sx={{ mr: 1 }} />
            For <strong>{row.student_name}</strong>{row.mentor_name && <> with <strong>{row.mentor_name}</strong></>}.
            {row.request_note && (
              <Box component="span" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                Context: "{row.request_note}"
              </Box>
            )}
            {editing && row.scheduled_at && (
              <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                Currently scheduled for {fmtDateTime(row.scheduled_at)}.
              </Box>
            )}
          </Typography>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <TextField
          fullWidth size="small" required
          type="datetime-local"
          label={editing ? 'New meeting date and time' : 'Meeting date and time'}
          value={scheduleAt}
          onChange={e => setScheduleAt(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth multiline rows={3} size="small"
          label="Optional note for the family (location, Zoom, agenda)"
          value={note}
          onChange={e => setNote(e.target.value)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Student and parent(s) will receive an email confirmation.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || !scheduleAt}>
          {saving ? <CircularProgress size={18} /> : (editing ? 'Save new time' : 'Set schedule')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Cancel dialog ────────────────────────────────────────────────────────────

function CancelDialog({
  row, onClose, onSaved,
}: { row: MeetingRow | null; onClose: () => void; onSaved: () => void }) {
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState<string | null>(null)

  useEffect(() => {
    if (row) { setReason(''); setError(null) }
  }, [row])

  async function save() {
    if (!row) return
    setSaving(true); setError(null)
    const { data, error: err } = await supabase.functions.invoke('cancel-meeting', {
      body: { meeting_id: row.meeting_id, reason: reason.trim() || undefined },
    })
    setSaving(false)
    if (err)         { setError(await readFunctionError(err)); return }
    if (data?.error) { setError(data.error); return }
    onSaved()
  }

  const wasScheduled = !!row?.scheduled_at

  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{wasScheduled ? 'Cancel scheduled meeting' : 'Cancel request'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {wasScheduled
            ? 'Cancels the meeting and notifies the student and parent(s) by email.'
            : 'Cancels the request. No email is sent because the parent was never notified.'}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <TextField
          fullWidth multiline rows={3} size="small"
          label="Reason (optional)"
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Keep meeting</Button>
        <Button variant="contained" color="warning" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Cancel meeting'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Mark held dialog ─────────────────────────────────────────────────────────

function MarkHeldDialog({
  row, onClose, onSaved,
}: { row: MeetingRow | null; onClose: () => void; onSaved: () => void }) {
  const [summary, setSummary] = useState('')
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState<string | null>(null)

  useEffect(() => {
    if (row) { setSummary(''); setError(null) }
  }, [row])

  async function save() {
    if (!row) return
    if (summary.trim().length < 30) { setError('Summary must be at least 30 characters.'); return }
    setSaving(true); setError(null)
    const { data, error: err } = await supabase.functions.invoke('mark-meeting-held', {
      body: { meeting_id: row.meeting_id, summary: summary.trim() },
    })
    setSaving(false)
    if (err)         { setError(await readFunctionError(err)); return }
    if (data?.error) { setError(data.error); return }
    onSaved()
  }

  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Mark meeting held</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Record what was discussed. For Step 1 review meetings, the submission goes back to draft
          for the student to refine.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <TextField
          fullWidth multiline rows={5} size="small" required
          label="Meeting summary (at least 30 characters)"
          value={summary}
          onChange={e => setSummary(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || summary.trim().length < 30}>
          {saving ? <CircularProgress size={18} /> : 'Mark held'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
